import { APIResponse } from '@core/types';
import { NetworkError } from '@shared/errors';

export class EditorAPI {
  private platformOrigin: string;
  private endpoints: Record<string, string>;

  constructor(
    platformOrigin: string,
    verifyEndpoint: string,
    listModificationsEndpoint?: string,
    saveModificationsBulkEndpoint?: string,
  ) {
    this.platformOrigin = platformOrigin.replace(/\/+$/, '');
    this.endpoints = {
      verify: verifyEndpoint,
      listModifications: listModificationsEndpoint || '/api/sdk/editor/modifications',
      saveModificationsBulk: saveModificationsBulkEndpoint || '/api/sdk/editor/modifications/bulk',
    };
  }

  async verifySession(token: string, pageUrl: string): Promise<APIResponse> {
    return this.post(this.endpoints.verify, { token, page_url: pageUrl });
  }

  async loadModifications(token: string, pageUrl: string): Promise<APIResponse> {
    return this.post(this.endpoints.listModifications || '/api/sdk/editor/modifications', {
      token,
      page_url: pageUrl,
    });
  }

  async saveModifications(
    token: string,
    pageUrl: string,
    modifications: any[],
  ): Promise<APIResponse> {
    const modificationsWithOrder = modifications.map((mod, index) => ({ ...mod, order: index }));

    return this.post(this.endpoints.saveModificationsBulk || '/api/sdk/editor/modifications/bulk', {
      token,
      page_url: pageUrl,
      modifications: modificationsWithOrder,
    });
  }

  private async post(endpoint: string, body: any): Promise<APIResponse> {
    const url = this.platformOrigin + endpoint;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      const contentType = response.headers.get('content-type') || '';
      const isJson = contentType.includes('application/json');
      const data = isJson ? await response.json().catch(() => null) : null;

      return {
        ok: response.ok && data?.ok === true,
        data,
        reason: data?.reason,
        meta: data?.meta,
      };
    } catch (error) {
      throw new NetworkError(
        `Network request failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        undefined,
        error,
      );
    }
  }
}
